import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, Zap, XCircle, Rocket } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const BoostSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const navigate = useNavigate();
  const { token, refreshUser } = useAuth();

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;
    const pollInterval = 2000;

    const pollPaymentStatus = async () => {
      try {
        const res = await axios.get(`${API}/boost/payment-status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setPaymentData(res.data);

        if (res.data.payment_status === 'paid') {
          setStatus('success');
          await refreshUser();
          toast.success('Boost aktiveret!');
          return;
        } else if (res.data.status === 'expired') {
          setStatus('error');
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollPaymentStatus, pollInterval);
        } else {
          setStatus('error');
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollPaymentStatus, pollInterval);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, token, refreshUser]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full bg-card rounded-3xl p-8 text-center border border-border shadow-xl"
      >
        {status === 'loading' && (
          <>
            <div className="w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-accent animate-spin" />
            </div>
            <h2 className="text-2xl font-display font-bold text-foreground mb-2">
              Aktiverer Boost
            </h2>
            <p className="text-muted-foreground">
              Vent venligst mens vi aktiverer din profil boost...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="w-20 h-20 rounded-full bg-gradient-to-br from-accent to-accent/70 flex items-center justify-center mx-auto mb-6"
            >
              <Rocket className="w-10 h-10 text-white" />
            </motion.div>
            <h2 className="text-2xl font-display font-bold text-foreground mb-2">
              Boost Aktiveret!
            </h2>
            <p className="text-muted-foreground mb-6">
              Din profil vises nu til flere personer. Udnyt det!
            </p>
            
            {paymentData && (
              <div className="bg-accent/10 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-center gap-2 text-accent">
                  <Zap className="w-5 h-5" />
                  <span className="font-semibold">Profil Boost Aktiv</span>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Link to="/discover" className="block">
                <Button className="w-full rounded-full bg-primary hover:bg-primary/90" data-testid="back-to-discover-btn">
                  <Zap className="w-5 h-5 mr-2" />
                  Tilbage til Opdag
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-destructive" />
            </div>
            <h2 className="text-2xl font-display font-bold text-foreground mb-2">
              Boost Fejlede
            </h2>
            <p className="text-muted-foreground mb-6">
              Vi kunne ikke aktivere din boost. Prøv venligst igen.
            </p>
            <div className="space-y-3">
              <Link to="/discover" className="block">
                <Button className="w-full rounded-full bg-primary hover:bg-primary/90" data-testid="try-again-btn">
                  Prøv Igen
                </Button>
              </Link>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default BoostSuccess;
